import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronLeft, Clipboard, CloudOff, Eye, RotateCw, Users } from "lucide-react";
import { TopBar, CoachRibbon, TextLink, NumberTile } from "./kit";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { usePageWidth } from "../context/PageWidthContext";
import { useAuth } from "../context/AuthContext";
import { homePathForRole, homeLabelForRole } from "../lib/homePath";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import { toStatsQuery } from "../lib/statsQuery";
import { avgText, srText, toRateText } from "../lib/recordFigures";
import { leftOutNote } from "../lib/overs";
import { coachPlayersName } from "../lib/coachPlayersName";

/**
 * Issue #1289 (round 12 CoachFullRecord, D298; D259; D301): the coach's full
 * record of ONE player at `/coach-portal/record?player=<id>`, in the new look.
 * `pages/CoachPlayerRecord.jsx` picks this for a coach with `coach-player` on;
 * an admin and the flag-off leg keep the shipped page (D217, D228.2).
 *
 * Figures only (D90): three career groups, then every match, newest first.
 * READ-ONLY, structurally: `api.get` and `getWithRetry` only, no form, no
 * dialog, no input. The three reads are the shipped page's, in the same order:
 * the roster first, and the stats and the matches only once the roster has
 * found the player (#720 m1: a denial sends no data request, D186.2).
 *
 * Two board details the API cannot supply, both built as the planner
 * recommended and ruled in D301: Highest carries no not-out star (S1), and a
 * matches-failed card exists although the board draws none (S2).
 */

const CARD = { background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" };
const SMALL_CONTROL = {
  display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", minHeight: 44, padding: "0 14px",
  background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)",
  fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", boxShadow: "none",
};
const MANROPE = "'Manrope', sans-serif";

function Skeleton({ count, testId }) {
  return (
    <div data-testid={testId} aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 120, boxSizing: "border-box" }}>
          <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

function IconCard({ testId, title, icon: Icon, children }) {
  return (
    <div data-testid={testId} style={{ ...CARD, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", color: "var(--nw-navy)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={22} aria-hidden="true" style={{ color: "var(--nw-navy)" }} />
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{title}</div>
      {children}
    </div>
  );
}

function FailedCard({ testId, title, onRetry }) {
  return (
    <div data-testid={testId} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{title}</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
        Nothing has been lost — we just couldn't reach the server.
      </div>
      <button type="button" data-testid={`${testId}-retry`} onClick={onRetry} style={SMALL_CONTROL}>
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

const TILE_BOX = { padding: "8px 10px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: 10 };

function Tile({ id, label, value, base }) {
  return <NumberTile variant="compact" data-testid={`cpr-stat-${id}`} label={label} value={value} restsOn={base} style={TILE_BOX} />;
}

function Group({ testId, title, children, ...wide }) {
  return (
    <section data-testid={testId} {...wide} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 12, padding: 14 }}>
      <h2 style={{ margin: 0, fontFamily: MANROPE, fontWeight: 700, fontSize: 16, color: "var(--nw-ink)" }}>{title}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>{children}</div>
    </section>
  );
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

function CareerGroups({ d }) {
  // The average, strike rate and economy are derived exactly as the player
  // page's snapshot derives them (the player-page snapshot, removed in #1221 slice 5): from the
  // counts the API already summed, never re-derived from the matches.
  const notOuts = Number(d.not_outs) || 0;
  const innings = Number(d.innings) || 0;
  const outs = innings - notOuts;
  const avg = outs > 0 ? (Number(d.total_runs) || 0) / outs : null;
  const balls = Number(d.total_balls_faced) || 0;
  const overs = Number(d.total_overs) || 0;
  const leftOut = leftOutNote(Number(d.overs_left_out) || 0);
  const econBase = overs > 0 ? `Rests on ${plural(overs, "over")}` : null;
  const econRestsOn = leftOut ? [econBase, `(${leftOut})`].filter(Boolean).join(" ") : econBase;
  return (
    // The groups stay one column on a phone and a tablet. From 1024 (index.css, `.nw-wide-grid`) the highlights
    // take a row and bowling sits beside fielding.
    <div className="nw-wide-grid" style={{ "--nw-wide-gap": "14px" }}>
      <Group testId="cpr-career-highlights" title="Career highlights" data-wide="full">
        <Tile id="matches" label="Matches" value={d.matches} />
        <Tile id="innings" label="Innings" value={d.innings} />
        <Tile id="total_runs" label="Runs" value={d.total_runs} />
        <Tile id="not_outs" label="Not outs" value={d.not_outs} />
        <Tile id="average" label="Average" value={avgText(avg)} base={outs > 0 ? `Rests on ${plural(outs, "out")}` : null} />
        <Tile id="sr" label="Strike rate" value={srText(d.sr)} base={balls > 0 ? `Off ${plural(balls, "ball")}` : null} />
        <Tile id="highest" label="Highest" value={d.highest} />
        <Tile id="fifties" label="50s" value={d.fifties} />
        <Tile id="hundreds" label="100s" value={d.hundreds} />
        <Tile id="fours" label="4s" value={d.fours} />
        <Tile id="sixes" label="6s" value={d.sixes} />
        <Tile id="total_wickets" label="Wickets" value={d.total_wickets} />
      </Group>
      <Group testId="cpr-career-bowling" title="Bowling record" data-wide-col="1">
        <Tile id="bowling-total_wickets" label="Wickets" value={d.total_wickets} />
        <Tile id="econ" label="Economy" value={toRateText(d.econ)} base={econRestsOn} />
        <Tile id="maidens" label="Maidens" value={d.maidens} />
        <Tile id="dot_balls" label="Dot balls" value={d.dot_balls} />
        <Tile id="wides" label="Wides" value={d.wides} />
        <Tile id="no_balls" label="No balls" value={d.no_balls} />
      </Group>
      <Group testId="cpr-career-fielding" title="Fielding & wicket-keeping" data-wide-col="2">
        <Tile id="catches" label="Catches" value={d.catches} />
        <Tile id="run_outs" label="Run outs" value={d.run_outs} />
        <Tile id="direct_hit_run_outs" label="Direct hits" value={d.direct_hit_run_outs} />
        <Tile id="assists" label="Assists" value={d.assists} />
        <Tile id="stumpings" label="Stumpings" value={d.stumpings} />
        <Tile id="byes_conceded" label="Byes conc." value={d.byes_conceded} />
        <Tile id="wicketkeeping_run_outs" label="Run outs (WK)" value={d.wicketkeeping_run_outs} />
        <Tile id="wicketkeeping_assists" label="Assists (WK)" value={d.wicketkeeping_assists} />
      </Group>
    </div>
  );
}

function StatsFailed() {
  return (
    <div
      data-testid="cpr-stats-error"
      role="alert"
      style={{ ...CARD, display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderLeft: "3px solid var(--nw-alert)", borderRadius: 10 }}
    >
      <AlertTriangle size={16} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink)" }}>Couldn't load this player's career figures</div>
        <div style={{ fontSize: 12, color: "var(--nw-ink-muted)" }}>The matches below still show.</div>
      </div>
    </div>
  );
}

// The match log is figures as sent; a missing one is a dash, never a 0 (D190).
const fig = (v) => (v === null || v === undefined ? "—" : v);

function MatchCard({ m }) {
  // #715: the not-out star belongs to an innings that happened.
  const star = m.batting?.runs != null && m.batting?.not_out ? "*" : "";
  const sub = [m.competition, m.season].filter(Boolean).join(" · ");
  const cells = [
    ["Runs", `${fig(m.batting?.runs)}${star}`],
    ["Balls", fig(m.batting?.balls_faced)],
    ["Wkts", fig(m.bowling?.wickets)],
    ["Econ", toRateText(m.bowling?.economy_rate)],
  ];
  return (
    <div data-testid={`cpr-match-card-${m.match_id}`} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontFamily: MANROPE, fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", minWidth: 0, overflowWrap: "anywhere" }}>v {m.opponent}</span>
        <span style={{ fontSize: 12, color: "var(--nw-ink-muted)", whiteSpace: "nowrap" }}>{m.match_date || "—"}</span>
      </div>
      {sub && <div style={{ fontSize: 12, color: "var(--nw-ink-muted)", overflowWrap: "anywhere" }}>{sub}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 6 }}>
        {cells.map(([label, value]) => (
          <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 10px", background: "var(--nw-line-faint)", borderRadius: 10, minWidth: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>{label}</span>
            <span style={{ fontFamily: MANROPE, fontWeight: 800, fontSize: 16, color: "var(--nw-ink)" }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Pure presentation of every state. `rosterState` is `loading | found | absent
 * | failed`; `matchesState` is `loading | loaded | failed`.
 */
export function CoachRecordBody({ playerId, rosterState, player, summary, summaryFailed, matches, matchesState, onRetry }) {
  const headingRef = useRef(null);
  const showFace = !!playerId && rosterState === "found";
  const showHeading = !playerId || showFace;

  useEffect(() => {
    if (showHeading && headingRef.current) headingRef.current.focus({ preventScroll: true });
  }, [showHeading]);

  const playerName = player?.full_name || "this player";
  const back = (
    <TextLink
      as={Link}
      to={playerId ? `/coach-portal?player=${encodeURIComponent(playerId)}` : "/coach-portal"}
      data-testid="cpr-back"
      style={{ alignSelf: "flex-start", gap: 6, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}
    >
      <ChevronLeft size={16} aria-hidden="true" />
      {coachPlayersName()}
    </TextLink>
  );
  const heading = (text) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>Read-only</div>
      <h1
        ref={headingRef}
        tabIndex={-1}
        data-testid="cpr-heading"
        style={{ margin: 0, fontFamily: MANROPE, fontWeight: 800, fontSize: 24, letterSpacing: "-0.5px", lineHeight: 1.15, color: "var(--nw-ink)", overflowWrap: "anywhere", outline: "none" }}
      >
        {text}
      </h1>
    </div>
  );

  if (!playerId) {
    return (
      <>
        {back}
        {heading("A player's record")}
        <IconCard testId="cpr-no-player" icon={Users} title="Choose a player first">
          <div className="nw-lede">Choose a player on {coachPlayersName()}, then open their record from the roster row.</div>
        </IconCard>
      </>
    );
  }
  // "Not on your roster", "revoked" and "not a player at all" are one card, for
  // the reason routes/authz.ts gives them one 404 (D186.2).
  if (rosterState === "absent") {
    return (
      <>
        {back}
        <IconCard testId="cpr-not-found" icon={Users} title="There is no record here for you to read.">
          <div className="nw-lede">
            A player's record opens once they have an active link with you — ask them to share, or pick someone from {coachPlayersName()}.
          </div>
        </IconCard>
      </>
    );
  }
  // Held until the roster answers: a reading notice for a player this coach may
  // turn out not to be linked to would be wrong in the one direction this page
  // must never be wrong in.
  if (rosterState === "loading") {
    return (
      <>
        {back}
        <Skeleton count={2} testId="cpr-loading" />
      </>
    );
  }
  if (rosterState === "failed") {
    return (
      <>
        {back}
        <FailedCard testId="cpr-roster-error" title="Couldn't load this player's record" onRetry={onRetry} />
      </>
    );
  }

  const hideCareer = matchesState === "loaded" && matches.length === 0;
  const rows = [...matches].sort((a, b) => String(b.match_date).localeCompare(String(a.match_date)));
  return (
    <>
      {back}
      {heading(`${playerName}'s record`)}
      <div
        data-testid="cpr-reading-notice"
        style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-navy-tint)", borderRadius: 10 }}
      >
        <Eye size={16} aria-hidden="true" style={{ color: "var(--nw-navy)", flexShrink: 0, marginTop: 2 }} />
        <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)", minWidth: 0 }}>
          You are reading {playerName}'s record. It is theirs to change — nothing on this page edits it.
        </span>
      </div>

      {/* No matches: a grid of noughts would read as a record (the board). */}
      {!hideCareer && (summaryFailed ? <StatsFailed /> : !summary ? <Skeleton count={1} testId="cpr-stats-loading" /> : <CareerGroups d={summary} />)}

      {matchesState === "loading" && <Skeleton count={2} testId="cpr-matches-loading" />}
      {matchesState === "failed" && <FailedCard testId="cpr-matches-error" title="Couldn't load this player's matches" onRetry={onRetry} />}
      {matchesState === "loaded" && rows.length === 0 && (
        <IconCard testId="cpr-matches-empty" icon={Clipboard} title={`${playerName} has not logged a match yet.`} />
      )}
      {matchesState === "loaded" && rows.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
            <div data-testid="cpr-matches" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)" }}>
              Matches · {rows.length}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--nw-navy)" }}>Newest first</div>
          </div>
          <div className="nw-match-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {rows.map((m) => <MatchCard key={m.match_id} m={m} />)}
          </div>
        </>
      )}
    </>
  );
}

export default function CoachRecordScreen() {
  const { user } = useAuth();
  const { setActive: setChromeless } = useChromelessOverride();
  // #1431 (archetype C, D386): the wide column; the career groups and the match cards sit two-up from 1024.
  usePageWidth("wide");
  const [sp] = useSearchParams();
  const playerId = sp.get("player");

  const [player, setPlayer] = useState(null);
  const [rosterState, setRosterState] = useState("loading"); // loading | found | absent | failed
  const [summary, setSummary] = useState(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchesState, setMatchesState] = useState("loading"); // loading | loaded | failed
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!playerId) return;
    let cancelled = false;
    setRosterState("loading");
    api
      .get("/players")
      .then((r) => {
        if (cancelled) return;
        const row = (r.data || []).find((p) => p.player_id === playerId);
        setPlayer(row || null);
        setRosterState(row ? "found" : "absent");
      })
      .catch((e) => {
        if (cancelled) return;
        // #271: "we could not reach the roster" is not "this player is not yours".
        setRosterState("failed");
        console.error("CoachRecordScreen: failed to load /players", e);
      });
    return () => { cancelled = true; };
  }, [playerId, reloadKey]);

  // Both data reads wait for the roster (#720 m1): a denial sends no data request.
  useEffect(() => {
    if (!playerId || rosterState !== "found") return;
    let cancelled = false;
    setSummaryFailed(false);
    getWithRetry(`/stats/summary${toStatsQuery({ playerId })}`)
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setSummary(null);
        setSummaryFailed(true);
        console.error("CoachRecordScreen: failed to load /stats/summary", e);
      });
    return () => { cancelled = true; };
  }, [playerId, rosterState, reloadKey]);

  useEffect(() => {
    if (!playerId || rosterState !== "found") return;
    let cancelled = false;
    setMatchesState("loading");
    getWithRetry(`/matches?player_id=${encodeURIComponent(playerId)}`)
      .then((r) => {
        if (cancelled) return;
        setMatches(r.data || []);
        setMatchesState("loaded");
      })
      .catch((e) => {
        if (cancelled) return;
        // An empty list after a failure would say the player has logged nothing (#271).
        setMatches([]);
        setMatchesState("failed");
        console.error("CoachRecordScreen: failed to load /matches", e);
      });
    return () => { cancelled = true; };
  }, [playerId, rosterState, reloadKey]);

  return (
    <div data-testid="coach-player-record" style={{ color: "var(--nw-ink)" }}>
      <TopBar
        variant="white"
        homeHref={homePathForRole(user?.role)}
        homeLabel={`CricketCompass — go to ${homeLabelForRole(user?.role)}`}
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="cpr-topbar"
      />
      <CoachRibbon current="players" />
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "16px 20px 20px" }}>
        <CoachRecordBody
          playerId={playerId}
          rosterState={rosterState}
          player={player}
          summary={summary}
          summaryFailed={summaryFailed}
          matches={matches}
          matchesState={matchesState}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </div>
    </div>
  );
}
